/**
 * TramCar.js — single rectangular tram-car primitive.
 *
 * Both `SnakeBody` and `PipelineTrain` need to render a rounded yellow tram
 * body with a darker front, a centered horizontal stripe, and circular
 * windows. This factory centralizes that visual; per-game logic (segment
 * routing, path interpolation) stays where it is.
 *
 * Public API:
 *   drawTramCarRect(g, rect, opts?)
 *     rect: { x, y, w, h }
 *     opts: { horizontal?: bool, forwardSign?: -1|+1, head?: bool, windows?: int }
 */

const BODY = 0xfacc15;
const FRONT = 0xeab308;
const STRIPE = 0xa16207;
const WINDOW = 0xbfe7ff;
const SHADOW = 0x000000;

export function drawTramCarRect(g, rect, opts = {}) {
    const {
        horizontal = true, forwardSign = 1,
        windows = 3, windowHighlights = false,
        bodyColor = BODY, frontColor = FRONT, stripeColor = STRIPE, windowColor = WINDOW,
        topHighlight = false,
        radius: customRadius = null,
    } = opts;
    const { x, y, w, h } = rect;
    const radius = customRadius != null
        ? customRadius
        : Math.min(Math.floor(Math.min(w, h) * 0.24), 16);

    g.roundRect(x + 2, y + 2, w, h, radius);
    g.fill({ color: SHADOW, alpha: 0.2 });

    g.roundRect(x, y, w, h, radius);
    g.fill(bodyColor);

    if (topHighlight) {
        g.roundRect(x, y, w, horizontal ? h * 0.4 : h, radius);
        g.fill({ color: 0xfef08a, alpha: 0.3 });
    }

    const frontDepth = horizontal ? Math.round(w * 0.28) : Math.round(h * 0.28);
    const frontX = horizontal ? (forwardSign >= 0 ? x + w - frontDepth : x) : x;
    const frontY = horizontal ? y : (forwardSign >= 0 ? y + h - frontDepth : y);
    const frontW = horizontal ? frontDepth : w;
    const frontH = horizontal ? h : frontDepth;
    g.roundRect(frontX, frontY, frontW, frontH, radius);
    g.fill(frontColor);

    const stripeInset = Math.max(3, Math.round(Math.min(w, h) * 0.14));
    if (horizontal) {
        g.roundRect(x + stripeInset, y + h * 0.5 - 2, w - stripeInset * 2, 4, 2);
    } else {
        g.roundRect(x + w * 0.5 - 2, y + stripeInset, 4, h - stripeInset * 2, 2);
    }
    g.fill(stripeColor);

    if (windows > 0) {
        const wW = horizontal ? Math.round(w * 0.13) : Math.round(w * 0.34);
        const wH = horizontal ? Math.round(h * 0.34) : Math.round(h * 0.13);
        const gap = Math.max(3, Math.round(Math.min(w, h) * 0.12));
        const cx = x + w / 2;
        const cy = y + h / 2;

        if (horizontal) {
            const total = wW * windows + gap * (windows - 1);
            let wx = cx - total / 2;
            const wy = cy - wH / 2;
            for (let i = 0; i < windows; i++) {
                g.roundRect(wx, wy, wW, wH, 3);
                g.fill(windowColor);
                if (windowHighlights) {
                    g.roundRect(wx, wy, wW, wH * 0.4, 3);
                    g.fill({ color: 0xe0f2fe, alpha: 0.4 });
                }
                wx += wW + gap;
            }
        } else {
            const total = wH * windows + gap * (windows - 1);
            let wy = cy - total / 2;
            const wx = cx - wW / 2;
            for (let i = 0; i < windows; i++) {
                g.roundRect(wx, wy, wW, wH, 3);
                g.fill(windowColor);
                if (windowHighlights) {
                    g.roundRect(wx, wy, wW * 0.4, wH, 3);
                    g.fill({ color: 0xe0f2fe, alpha: 0.4 });
                }
                wy += wH + gap;
            }
        }
    }
}

export const TRAM_COLORS = { body: BODY, front: FRONT, stripe: STRIPE, window: WINDOW };
